import React, { useState, useEffect, useCallback, useRef } from "react";
import { MessageSquare, Plus, Paperclip, Send, ArrowLeft, Loader2, CheckCircle2, RotateCcw, X } from "lucide-react";

const fmtTs = (s) => { try { return new Date(s).toLocaleString(); } catch { return s; } };

export const SupportMessaging = ({ client, isAdmin = false, accent = "#B8860B" }) => {
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [loadingThread, setLoadingThread] = useState(false);
  const [composeNew, setComposeNew] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");
  const [recipients, setRecipients] = useState([]);
  const [recipientId, setRecipientId] = useState("");
  const fileRef = useRef();
  const activeId = active?.id;

  const loadThreads = useCallback(async () => {
    try { const r = await client.get("/support/threads"); setThreads(r.data.threads || []); } catch { /* noop */ }
  }, [client]);

  const openThread = async (id) => {
    setLoadingThread(true); setComposeNew(false); setErr("");
    try { const r = await client.get(`/support/threads/${id}`); setActive(r.data.thread); } catch { /* noop */ }
    setLoadingThread(false);
    loadThreads();
  };

  useEffect(() => {
    loadThreads();
    const t = setInterval(() => {
      loadThreads();
      if (activeId) client.get(`/support/threads/${activeId}`).then((r) => setActive(r.data.thread)).catch(() => {});
    }, 15000);
    return () => clearInterval(t);
  }, [loadThreads, activeId, client]);

  const resetCompose = () => { setSubject(""); setBody(""); setFile(null); setRecipientId(""); if (fileRef.current) fileRef.current.value = ""; };

  const startCompose = async () => {
    setComposeNew(true); setActive(null); resetCompose();
    if (isAdmin) {
      try { const r = await client.get("/support/admin/recipients"); setRecipients(r.data.recipients || []); } catch { /* noop */ }
    }
  };

  const submitNew = async () => {
    if (!subject.trim() || !body.trim()) { setErr("Subject and message are required."); return; }
    if (isAdmin && !recipientId) { setErr("Choose a recipient."); return; }
    setSending(true); setErr("");
    try {
      const fd = new FormData(); fd.append("subject", subject); fd.append("body", body); if (file) fd.append("file", file);
      let r;
      if (isAdmin) { fd.append("party_id", recipientId); r = await client.post("/support/admin/threads", fd); }
      else { r = await client.post("/support/threads", fd); }
      resetCompose(); setComposeNew(false);
      await loadThreads();
      openThread(r.data.thread.id);
    } catch (e) { setErr(e?.response?.data?.detail || "Could not send. Try again."); }
    setSending(false);
  };

  const submitReply = async () => {
    if (!body.trim() && !file) { setErr("Message cannot be empty."); return; }
    setSending(true); setErr("");
    try {
      const fd = new FormData(); fd.append("body", body); if (file) fd.append("file", file);
      await client.post(`/support/threads/${active.id}/messages`, fd);
      resetCompose();
      openThread(active.id);
    } catch (e) { setErr(e?.response?.data?.detail || "Could not send. Try again."); }
    setSending(false);
  };

  const setStatus = async (status) => {
    const fd = new FormData(); fd.append("status", status);
    await client.post(`/support/threads/${active.id}/status`, fd);
    openThread(active.id);
  };

  const openAttachment = async (att) => {
    try {
      const r = await client.get(`/support/attachments/${att.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { /* noop */ }
  };

  const card = "bg-[#13130E] border border-[#2A2A22] rounded-xl";
  const totalUnread = threads.reduce((s, t) => s + (t.unread || 0), 0);

  return (
    <section className={`${card} overflow-hidden`} data-testid="support-panel">
      <div className="px-5 py-4 border-b border-[#2A2A22] flex items-center gap-3">
        <MessageSquare size={18} style={{ color: accent }} />
        <h3 className="font-serif text-lg text-[#ECECE4]">{isAdmin ? "Support Inbox" : "Messages & Support"}</h3>
        {totalUnread > 0 && (
          <span className="px-2 py-0.5 text-[10px] font-bold rounded-full text-black" style={{ background: accent }} data-testid="support-unread-badge">{totalUnread} new</span>
        )}
        {!active && (
          <button onClick={startCompose} data-testid="support-new-thread-btn"
            className="ml-auto inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.1em] font-bold px-3 py-1.5 rounded-md text-black" style={{ background: accent }}>
            <Plus size={13} /> {isAdmin ? "New message" : "New message"}
          </button>
        )}
      </div>

      <div className="grid md:grid-cols-[280px_1fr] min-h-[320px]">
        {/* Thread list */}
        <div className="border-r border-[#2A2A22] max-h-[460px] overflow-y-auto">
          {threads.length === 0 ? (
            <div className="p-5 text-[12px] text-[#5C5C5C]" data-testid="support-empty">
              {isAdmin ? "No support threads yet." : "No messages yet. Start a conversation with our team."}
            </div>
          ) : threads.map((t) => (
            <button key={t.id} onClick={() => openThread(t.id)} data-testid={`support-thread-${t.id}`}
              className={`w-full text-left px-4 py-3 border-b border-[#1F1F18] hover:bg-[#181811] ${active?.id === t.id ? "bg-[#181811]" : ""}`}>
              <div className="flex items-center gap-2">
                <span className="text-[13px] text-[#ECECE4] truncate flex-1">{t.subject}</span>
                {t.channel === "internal" && <span className="text-[8px] uppercase tracking-[0.12em] px-1.5 py-0.5 rounded-sm bg-[#0e2a6b]/40 text-[#9DB4E8] shrink-0" data-testid={`support-team-badge-${t.id}`}>Team</span>}
                {t.unread > 0 && <span className="w-2 h-2 rounded-full shrink-0" style={{ background: accent }} data-testid={`support-thread-unread-${t.id}`} />}
              </div>
              {isAdmin && <div className="text-[10px] text-[#8A8A8A] mt-0.5">{t.party_name} · {t.party_type}</div>}
              <div className="text-[11px] text-[#6C6C64] truncate mt-0.5">{t.last_message?.body || "—"}</div>
              <div className="flex items-center gap-2 mt-1">
                <span className={`text-[9px] uppercase tracking-[0.1em] px-1.5 py-0.5 rounded-sm ${t.status === "closed" ? "bg-[#3A2A2A] text-[#C99] " : "bg-[#0B6B4F]/20 text-[#3DBB8E]"}`}>{t.status}</span>
                <span className="text-[9px] text-[#5C5C5C]">{fmtTs(t.updated_at)}</span>
              </div>
            </button>
          ))}
        </div>

        {/* Detail / composer */}
        <div className="p-5 flex flex-col">
          {composeNew ? (
            <div className="flex flex-col gap-3" data-testid="support-compose-new">
              {isAdmin && (
                <select value={recipientId} onChange={(e) => setRecipientId(e.target.value)} data-testid="support-recipient-select"
                  className="bg-white text-black border border-[#D8D8D0] rounded px-3 py-2 text-[13px]">
                  <option value="">Select a borrower, broker, or team member…</option>
                  {recipients.some((r) => r.role === "admin") && (
                    <optgroup label="Team (admins)">
                      {recipients.filter((r) => r.role === "admin").map((r) => (
                        <option key={r.id} value={r.id}>👤 {r.name || r.email} · team ({r.email})</option>
                      ))}
                    </optgroup>
                  )}
                  <optgroup label="Borrowers & brokers">
                    {recipients.filter((r) => r.role !== "admin").map((r) => (
                      <option key={r.id} value={r.id}>{r.name || r.email} · {r.role} ({r.email})</option>
                    ))}
                  </optgroup>
                </select>
              )}
              <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" data-testid="support-subject-input"
                className="bg-white text-black border border-[#D8D8D0] rounded px-3 py-2 text-[13px]" />
              <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={5} placeholder="How can our team help you?" data-testid="support-body-input"
                className="bg-white text-black border border-[#D8D8D0] rounded px-3 py-2 text-[13px] resize-y" />
              <div className="flex items-center gap-3 flex-wrap">
                <label className="inline-flex items-center gap-1.5 text-[11px] text-[#9DB4E8] cursor-pointer">
                  <Paperclip size={13} /> {file ? file.name : "Attach file"}
                  <input ref={fileRef} type="file" className="hidden" data-testid="support-file-input" onChange={(e) => setFile(e.target.files?.[0] || null)} />
                </label>
                {file && <button onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ""; }} className="text-[#FF8585]"><X size={13} /></button>}
                <button onClick={submitNew} disabled={sending} data-testid="support-send-btn"
                  className="ml-auto inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.1em] font-bold px-4 py-2 rounded-md text-black disabled:opacity-50" style={{ background: accent }}>
                  {sending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Send
                </button>
                <button onClick={() => { setComposeNew(false); resetCompose(); }} className="text-[11px] text-[#8A8A8A]">Cancel</button>
              </div>
              {err && <div className="text-[11px] text-[#FF8585]">{err}</div>}
            </div>
          ) : active ? (
            <>
              <div className="flex items-center gap-2 mb-3">
                <button onClick={() => setActive(null)} className="md:hidden text-[#8A8A8A]"><ArrowLeft size={16} /></button>
                <div className="flex-1">
                  <div className="text-[14px] text-[#ECECE4] font-medium">{active.subject}</div>
                  {isAdmin && <div className="text-[11px] text-[#8A8A8A]">{active.channel === "internal" ? <>{active.counterpart_name || active.party_name} · team member</> : <>{active.party_name} · {active.party_email} · {active.party_type}</>}</div>}
                </div>
                {active.status === "open" ? (
                  <button onClick={() => setStatus("closed")} data-testid="support-close-btn" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.1em] text-[#C99] hover:text-[#e0a] "><CheckCircle2 size={12} /> Close</button>
                ) : (
                  <button onClick={() => setStatus("open")} data-testid="support-reopen-btn" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.1em] text-[#3DBB8E]"><RotateCcw size={12} /> Reopen</button>
                )}
              </div>

              <div className="flex-1 max-h-[320px] overflow-y-auto space-y-3 pr-1" data-testid="support-messages">
                {loadingThread ? <Loader2 className="animate-spin text-[#8A8A8A]" /> : (active.messages || []).map((m) => {
                  const mine = (m.sender_id && active.viewer_id) ? m.sender_id === active.viewer_id : (isAdmin ? m.sender_role === "admin" : m.sender_role !== "admin");
                  return (
                    <div key={m.id} data-testid={`support-message-${m.id}`} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[80%] rounded-lg px-3 py-2 ${mine ? "bg-[#1d3b2f] text-[#EAF6EF]" : "bg-[#22221A] text-[#ECECE4]"}`}>
                        <div className="text-[9px] uppercase tracking-[0.1em] text-[#9A9A90] mb-1">{m.sender_name || m.sender_role} · {fmtTs(m.created_at)}</div>
                        {m.body && <div className="text-[13px] whitespace-pre-wrap">{m.body}</div>}
                        {(m.attachments || []).map((a) => (
                          <button key={a.id} onClick={() => openAttachment(a)} data-testid={`support-attachment-${a.id}`}
                            className="mt-1.5 inline-flex items-center gap-1.5 text-[11px] text-[#9DB4E8] hover:underline">
                            <Paperclip size={12} /> {a.original_filename} <span className="text-[#5C5C5C]">({Math.ceil((a.size || 0) / 1024)} KB)</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>

              {active.status === "open" ? (
                <div className="mt-3 border-t border-[#2A2A22] pt-3 flex flex-col gap-2">
                  <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={2} placeholder="Type your reply…" data-testid="support-reply-input"
                    className="bg-white text-black border border-[#D8D8D0] rounded px-3 py-2 text-[13px] resize-y" />
                  <div className="flex items-center gap-3">
                    <label className="inline-flex items-center gap-1.5 text-[11px] text-[#9DB4E8] cursor-pointer">
                      <Paperclip size={13} /> {file ? file.name : "Attach"}
                      <input ref={fileRef} type="file" className="hidden" data-testid="support-reply-file-input" onChange={(e) => setFile(e.target.files?.[0] || null)} />
                    </label>
                    <button onClick={submitReply} disabled={sending} data-testid="support-reply-send-btn"
                      className="ml-auto inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.1em] font-bold px-4 py-2 rounded-md text-black disabled:opacity-50" style={{ background: accent }}>
                      {sending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Send
                    </button>
                  </div>
                  {err && <div className="text-[11px] text-[#FF8585]">{err}</div>}
                </div>
              ) : (
                <div className="mt-3 text-[11px] text-[#8A8A8A] border-t border-[#2A2A22] pt-3">This thread is closed. Reopen it to continue the conversation.</div>
              )}
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-[12px] text-[#5C5C5C]">
              {isAdmin ? "Select a thread to read and reply." : "Select a message, or start a new one."}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
